import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Play, ChevronRight, ChevronDown, 
  MessageSquare, FileText, Users, Shield, Zap, CheckCircle, Clock
} from 'lucide-react';

const clickPath = [
  { step: 1, action: 'Start at Control Tower', path: '/control-tower', desc: 'View active cases and system health' },
  { step: 2, action: 'Click "John Smith Onboarding" case', path: '/case/case_01HXYZ123ABC', desc: 'Review case detail, timeline, cluster' },
  { step: 3, action: 'Click "Review Bundle" button', path: '/case/case_01HXYZ123ABC/review', desc: 'Review actions, blast radius, policy checks' },
  { step: 4, action: 'Click "Approve and Execute"', path: null, desc: 'Watch execution progress' },
  { step: 5, action: 'View closed case with evidence', path: null, desc: 'See audit trail and ticket projection' }
];

const Script90s = () => (
  <div className="space-y-4 text-sm">
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:00</span>
      <p className="text-gray-700">Sarah, an HR manager, messages in Slack: "Please onboard John Smith, starting Monday in Engineering, reporting to Mary Jones."</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:10</span>
      <p className="text-gray-700">Vedaya acknowledges the request and creates a case. Case ID: #JML-2024-0847. Type: New Hire Onboarding.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:20</span>
      <p className="text-gray-700">The case view shows: Signal source (Slack), Subject (John Smith), Intent (Onboard to Engineering). A cluster of agents forms: Identity, Endpoint, SaaS.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:35</span>
      <p className="text-gray-700">An action bundle appears with 6 actions: Create Okta user, add to engineering group, provision Google Workspace, send Intune enrollment invite, invite to Slack, add to GitHub org.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:50</span>
      <p className="text-gray-700">Policy checks run. All pass: HR record confirmed, manager exists, budget approved. Bundle auto-approved (all low-risk actions).</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">1:05</span>
      <p className="text-gray-700">Execution begins. Progress bar shows 2/6 complete. Okta user created. Groups assigned.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">1:15</span>
      <p className="text-gray-700">All 6 actions complete. Verification gates pass. Case status: Closed.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">1:25</span>
      <p className="text-gray-700">A ticket is projected to Jira: ITHELP-2847. Full evidence bundle attached. Sarah receives confirmation in Slack.</p>
    </div>
  </div>
);

const Script5min = () => (
  <div className="space-y-4 text-sm">
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:00</span>
      <p className="text-gray-700">Introduction to the Control Tower. Overview of active cases, pending approvals, agent fleet health, connector status.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">0:30</span>
      <p className="text-gray-700">Slack message intake. Sarah sends request. Show how Vedaya parses intent: subject, action, department, manager.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">1:00</span>
      <p className="text-gray-700">Case creation. Walk through the case detail page: source, subject, intent parameters, SLA timer, cluster formation.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">1:30</span>
      <p className="text-gray-700">Action bundle formation. Show each action: connector, operation, parameters, risk tier. Explain idempotency keys.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">2:00</span>
      <p className="text-gray-700">Policy evaluation. Show policy checks panel. Explain pass/fail logic. Show what happens when a check fails (blocked with evidence).</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">2:30</span>
      <p className="text-gray-700">Approval flow. For this case, auto-approved. Show an example of a high-risk case that requires manager + security approval.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">3:00</span>
      <p className="text-gray-700">Execution. Watch actions execute in sequence. Progress indicators. Evidence collection at each step.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">3:30</span>
      <p className="text-gray-700">Verification gates. Explain pre-flight, canary, health check, full rollout. Show verification outcomes.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">4:00</span>
      <p className="text-gray-700">Rollback plan. Show what rollback looks like. Demonstrate a simulated rollback scenario.</p>
    </div>
    <div className="flex gap-3">
      <span className="text-[#4a70ff] font-mono w-16 shrink-0">4:30</span>
      <p className="text-gray-700">Case closure. Ticket projection to Jira. Evidence bundle attachment. Final confirmation to requestor. Metrics captured: time to resolution, cost.</p>
    </div>
  </div>
);

export const Demo = () => {
  const [activeScript, setActiveScript] = useState('90s');
  const [showClickPath, setShowClickPath] = useState(false);

  return (
    <div className="min-h-screen bg-white" data-testid="demo-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-[#4a70ff] flex items-center justify-center">
                <span className="text-white font-bold text-xs">V</span>
              </div>
              <span className="text-base font-semibold text-black">Vedaya</span>
            </Link>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/workflows" className="text-sm text-gray-600 hover:text-black">Workflows</Link>
              <Link to="/autonomy" className="text-sm text-gray-600 hover:text-black">Autonomy</Link>
              <Link to="/integrations" className="text-sm text-gray-600 hover:text-black">Integrations</Link>
              <Link to="/evidence" className="text-sm text-gray-600 hover:text-black">Evidence</Link>
              <Link to="/security-governance" className="text-sm text-gray-600 hover:text-black">Security</Link>
              <Link to="/pilot" className="text-sm text-gray-600 hover:text-black">Pilot</Link>
              <Link to="/pricing" className="text-sm text-gray-600 hover:text-black">Pricing</Link>
            </nav>
            <Link
              to="/control-tower"
              className="px-4 py-2 text-sm font-medium text-white bg-[#4a70ff] rounded-lg hover:bg-[#3a5fe6] transition-colors"
            >
              Open Demo
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-6 bg-gray-50 border-b border-gray-200">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-semibold text-black mb-4">
            Watch Vedaya automate one onboarding case.
          </h1>
          <p className="text-lg text-gray-600">
            From Slack request to closed ticket in 4 minutes.
          </p>
        </div>
      </section>

      {/* Video Placeholder */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="aspect-video bg-gray-900 rounded-xl flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
                <Play className="w-8 h-8 text-white" />
              </div>
              <p className="text-white/60 text-sm">JML Walkthrough Video</p>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Flow Overview */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-black mb-6 text-center">The JML Autopilot Flow</h2>
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {[
              { icon: MessageSquare, label: 'Slack Intake' },
              { icon: FileText, label: 'Case Created' },
              { icon: Users, label: 'Cluster Forms' },
              { icon: Zap, label: 'Bundle Built' },
              { icon: Shield, label: 'Policy Check' },
              { icon: CheckCircle, label: 'Execute' },
              { icon: Clock, label: 'Verify' }
            ].map((step, idx) => (
              <React.Fragment key={step.label}>
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center shadow-sm">
                    <step.icon className="w-5 h-5 text-[#4a70ff]" />
                  </div>
                  <span className="text-xs text-gray-500 mt-1">{step.label}</span>
                </div>
                {idx < 6 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* Walkthrough Scripts */}
      <section className="py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-xl font-bold text-black mb-6">Walkthrough Scripts</h2>
          
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setActiveScript('90s')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeScript === '90s' 
                  ? 'bg-[#4a70ff] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              90-Second Tour
            </button>
            <button
              onClick={() => setActiveScript('5min')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeScript === '5min' 
                  ? 'bg-[#4a70ff] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              5-Minute Deep Dive
            </button>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {activeScript === '90s' ? <Script90s /> : <Script5min />}
          </div>
        </div>
      </section>

      {/* Click Path */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setShowClickPath(!showClickPath)}
            className="w-full flex items-center justify-between mb-4"
          >
            <h2 className="text-xl font-bold text-black">Try It Yourself: Click Path</h2>
            {showClickPath ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
          </button>
          
          {showClickPath && (
            <div className="space-y-3">
              {clickPath.map((item) => (
                <div key={item.step} className="flex items-start gap-4 bg-white rounded-xl border border-gray-200 p-4">
                  <div className="w-8 h-8 rounded-full bg-[#4a70ff]/10 flex items-center justify-center shrink-0">
                    <span className="text-[#4a70ff] font-bold text-sm">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{item.action}</div>
                    <div className="text-sm text-gray-500">{item.desc}</div>
                  </div>
                  {item.path && (
                    <Link
                      to={item.path}
                      className="px-3 py-1.5 text-sm text-[#4a70ff] border border-[#4a70ff]/30 rounded-lg hover:bg-[#4a70ff]/5 transition-colors"
                    >
                      Go
                    </Link>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#4a70ff]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to explore?</h2>
          <p className="text-white/80 mb-6">
            Open the Control Tower and follow the click path above.
          </p>
          <Link
            to="/control-tower"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#4a70ff] font-medium rounded-lg hover:bg-gray-100 transition-colors"
          >
            Open Control Tower Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-gray-200">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-[#4a70ff] flex items-center justify-center">
              <span className="text-white font-bold text-xs">V</span>
            </div>
            <span className="text-sm text-gray-500">Vedaya AI</span>
          </div>
          <p className="text-sm text-gray-400">2026 Vedaya AI</p>
        </div>
      </footer>
    </div>
  );
};

export default Demo;
